// PRO+ "Video options" for the share wizard: strip the HiiKER furniture out of a flyover or trailer
// before it renders — no corner logo, no stats panel, no intro/outro cards — and, on a recording
// flyover only, leave the walker's photos out (a trailer is of a hike and has none).
//
// Everything deterministic lives here rather than in FlyoverShare.vue: the option list, the copy
// rules, the entitlement mirror and the per-user persistence. The same three rules are implemented
// on iOS (`FlyoverVideoOptions.swift`) and Android (`FlyoverVideoOptions.kt`), so the wording and
// the wire names have to be pinned in one testable place per platform rather than inlined in a view.
//
// The entitlement check here is a MIRROR, not the gate. The real one is server-side
// (`Flyover::RenderOptions.for`), which downgrades a non-PRO+ caller to the branded default whatever
// the client sends. This copy exists so the UI shows the truth before the render starts.

import { i18n } from '../../i18n';

/** Wire names, matching the GraphQL FlyoverVideoOptionsInput fields and the two renderers' config. */
export interface FlyoverVideoOptions {
  hideLogo: boolean;
  hideStats: boolean;
  skipIntro: boolean;
  hidePhotos: boolean;
}

export type FlyoverVideoOptionKey = keyof FlyoverVideoOptions;

/** The fully branded video: what every render was before this feature, and the default for everyone. */
export const DEFAULT_VIDEO_OPTIONS: Readonly<FlyoverVideoOptions> = Object.freeze({
  hideLogo: false,
  hideStats: false,
  skipIntro: false,
  hidePhotos: false,
});

export interface VideoOptionRow {
  key: FlyoverVideoOptionKey;
  label: string;
  sublabel: string;
  /** Only offered on a recording flyover — a hike trailer has no photos to hide. */
  flyoverOnly?: boolean;
}

/** Every row, in the order the design lays them out. Use videoOptionRowsFor to get one wizard's set. */
export const VIDEO_OPTION_ROWS: readonly VideoOptionRow[] = Object.freeze([
  {
    key: 'hideLogo',
    get label() { return i18n.global.t('flyover_share.video_options_helper.hide_logo'); },
    get sublabel() { return i18n.global.t('flyover_share.video_options_helper.hide_logo_sublabel'); },
  },
  {
    key: 'hideStats',
    get label() { return i18n.global.t('flyover_share.video_options_helper.hide_stats'); },
    get sublabel() { return i18n.global.t('flyover_share.video_options_helper.hide_stats_sublabel'); },
  },
  {
    key: 'skipIntro',
    get label() { return i18n.global.t('flyover_share.video_options_helper.skip_intro'); },
    get sublabel() { return i18n.global.t('flyover_share.video_options_helper.skip_intro_sublabel'); },
  },
  {
    key: 'hidePhotos',
    flyoverOnly: true,
    get label() { return i18n.global.t('flyover_share.video_options_helper.hide_photos'); },
    get sublabel() { return i18n.global.t('flyover_share.video_options_helper.hide_photos_sublabel'); },
  },
] as const);

/**
 * The rows one wizard shows: all four for a recording flyover, three for a hike trailer (it has no
 * photos to hide). Only the skip-intro sub-label names the video — "Starts straight into the flyover"
 * — so a trailer also swaps that one line and keeps the rest.
 */
export function videoOptionRowsFor(isTrailer: boolean): readonly VideoOptionRow[] {
  const rows = VIDEO_OPTION_ROWS.filter((row) => !(isTrailer && row.flyoverOnly));
  if (!isTrailer) return rows;
  return rows.map((row) => (row.key !== 'skipIntro' ? row : {
    key: row.key,
    get label() { return row.label; },
    get sublabel() { return i18n.global.t('flyover_share.trailer_share.skip_intro_sublabel'); },
  }));
}

function keysFor(isTrailer: boolean): FlyoverVideoOptionKey[] {
  return videoOptionRowsFor(isTrailer).map((row) => row.key);
}

/** How many of this wizard's options are on. Drives every piece of copy below. */
export function removedCount(options: FlyoverVideoOptions, isTrailer = false): number {
  return keysFor(isTrailer).filter((key) => options[key]).length;
}

export function isDefaultOptions(options: FlyoverVideoOptions, isTrailer = false): boolean {
  return removedCount(options, isTrailer) === 0;
}

/** The set as this wizard sends it: a trailer never carries hidePhotos, which it has no use for. */
export function optionsFor(options: FlyoverVideoOptions, isTrailer: boolean): FlyoverVideoOptions {
  return isTrailer ? { ...options, hidePhotos: false } : { ...options };
}

/**
 * The sentence under the options panel. Three states, not a template with a pluralised noun: at 0 it
 * has to say what the video WILL have (the panel is otherwise a row of switches with no stated
 * consequence), and with everything on "4 of 4 elements removed" reads like an error rather than the
 * thing the member deliberately asked for.
 */
export function optionsSummary(options: FlyoverVideoOptions, isTrailer = false): string {
  const count = removedCount(options, isTrailer);
  const total = keysFor(isTrailer).length;
  if (count === 0) {
    return isTrailer
      ? i18n.global.t('flyover_share.video_options_helper.summary_default')
      : i18n.global.t('flyover_share.video_options_helper.summary_default_photos');
  }
  if (count === total) {
    // "Just the flyover, nothing else" names the video, so a trailer has its own sentence.
    return isTrailer
      ? i18n.global.t('flyover_share.trailer_share.summary_bare_bones')
      : i18n.global.t('flyover_share.video_options_helper.summary_bare_bones');
  }
  return i18n.global.t('flyover_share.video_options_helper.summary_some_removed', { count, total });
}

/**
 * The trailing label on the collapsed row. Short, because it sits in an 11px pill next to a chevron
 * — the full summary sentence above would wrap the row onto three lines. State is never hidden: a
 * member who collapsed the panel can still see that something is set.
 */
export function collapsedOptionsLabel(options: FlyoverVideoOptions, isTrailer = false): string {
  const count = removedCount(options, isTrailer);
  const total = keysFor(isTrailer).length;
  if (count === 0) return i18n.global.t('flyover_share.video_options_helper.collapsed_default');
  if (count === total) return i18n.global.t('flyover_share.video_options_helper.collapsed_bare_bones');
  return i18n.global.t('flyover_share.video_options_helper.collapsed_some_removed', { count, total });
}

/** Flip one option. Returns a new object rather than mutating, so Vue sees the change. */
export function toggleOption(
  options: FlyoverVideoOptions,
  key: FlyoverVideoOptionKey,
): FlyoverVideoOptions {
  return { ...options, [key]: !options[key] };
}

/**
 * What this user is actually allowed. Mirrors the server: a free account renders the branded video
 * however much the UI is carrying, so anything read back from storage (or left over from a session
 * where they were subscribed) collapses to the default rather than being sent and silently dropped.
 */
export function allowedOptions(options: FlyoverVideoOptions, isPro: boolean): FlyoverVideoOptions {
  return isPro ? { ...options } : { ...DEFAULT_VIDEO_OPTIONS };
}

/** Coerce anything (stored JSON, a partial object, null) into a complete option set. */
export function normalizeOptions(raw: unknown): FlyoverVideoOptions {
  const source = (raw ?? {}) as Partial<Record<FlyoverVideoOptionKey, unknown>>;
  return {
    hideLogo: source.hideLogo === true,
    hideStats: source.hideStats === true,
    skipIntro: source.skipIntro === true,
    hidePhotos: source.hidePhotos === true,
  };
}

// --- persistence ---------------------------------------------------------------------------------
// "Remember the last-used option set and pre-fill it on the next export." Per browser rather than on
// the account: it is a per-export preference, not a profile setting, and pushing it through the
// launch-sync payload would make a share-sheet toggle an account-wide write.

export const VIDEO_OPTIONS_STORAGE_KEY = 'hiiker.flyover.videoOptions';

export function loadStoredOptions(storage: Storage | null = safeStorage()): FlyoverVideoOptions {
  try {
    const raw = storage?.getItem(VIDEO_OPTIONS_STORAGE_KEY);
    return raw ? normalizeOptions(JSON.parse(raw)) : { ...DEFAULT_VIDEO_OPTIONS };
  } catch {
    // Private windows and blocked site data both throw here; the branded default is the right
    // fallback, and a share must never fail because a preference could not be read.
    return { ...DEFAULT_VIDEO_OPTIONS };
  }
}

export function saveStoredOptions(
  options: FlyoverVideoOptions,
  storage: Storage | null = safeStorage(),
): void {
  try {
    storage?.setItem(VIDEO_OPTIONS_STORAGE_KEY, JSON.stringify(normalizeOptions(options)));
  } catch {
    /* preference only — never break the share */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
